import { useEffect, useRef } from 'react';
import { trackButtonClick } from '../../../utils/stats';

export default function Solutions() {
  const sectionRef = useRef<HTMLElement>(null);
  const lastTrackTimeRef = useRef(0);

  const solutions = [
    {
      icon: 'ri-team-line',
      title: '人机协同，重构服务',
      descriptions: [
        'AI 优先接高频、标准问题',
        '人工聚焦复杂、高价值场景',
        '7*24 小时全天候服务，转人工可控',
      ],
    },
    {
      icon: 'ri-database-2-line',
      title: '经验沉淀，能力递增',
      descriptions: [
        '人工优质经验持续沉淀',
        '知识不零散，告别个人依赖',
        '服务能力随业务同步放大',
      ],
    },
    {
      icon: 'ri-robot-line',
      title: 'AI上岗，堪比专家',
      descriptions: [
        'AI 原生应用，智能化程度高',
        '拟人化强，用户体验更丝滑',
        '技能丰富，支持外接MCP工具',
      ],
    },
    {
      icon: 'ri-shield-check-line',
      title: 'AI质检，持续优化',
      descriptions: [
        '持续评估会话质量',
        '识别风险与服务盲区',
        '辅助管理者发现问题趋势',
      ],
    },
    {
      icon: 'ri-settings-3-line',
      title: 'AI运营，落地见效',
      descriptions: [
        '指派运营专员负责日常运维',
        '提供知识清洗与结构化服务',
        '定期评估 AI 表现，数据驱动价值量化',
      ],
    },
    {
      icon: 'ri-line-chart-line',
      title: '数据驱动，赋能增长',
      descriptions: [
        '全量分析对话，覆盖服务全流程',
        '识别高频诉求与业务机会',
        '输出可执行的运营优化建议',
      ],
    }
  ];

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio >= 1) {
            const now = Date.now();
            if (now - lastTrackTimeRef.current >= 3000) {
              trackButtonClick('solutions_vis', '首页Solutions组件曝光');
              lastTrackTimeRef.current = now;
            }
          }
        });
      },
      { threshold: 1 }
    );

    observer.observe(section);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-16 sm:py-20 md:py-24 bg-gradient-to-b from-[#f8fbff] to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-12 sm:mb-14 md:mb-16">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3 sm:mb-4">
            我们是怎么解决的
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6 md:gap-8">
          {solutions.map((solution, index) => (
            <div
              key={index}
              className="group relative bg-white rounded-2xl p-6 sm:p-7 md:p-8 hover:shadow-2xl transition-all hover:-translate-y-2 border border-gray-100 flex flex-col h-full"
            >
              <div className="absolute top-0 right-0 w-32 h-32 bg-blue-100/30 rounded-full blur-3xl"></div>
              <div className="relative flex flex-col h-full">
                <div className="flex items-center gap-4 mb-4 sm:mb-5">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 flex-shrink-0 flex items-center justify-center bg-gradient-to-br from-[#0066ff] to-[#00a3ff] rounded-2xl group-hover:scale-110 transition-transform">
                    <span className="text-xl sm:text-2xl font-bold text-white">{index + 1}</span>
                  </div>
                  <h3 className="text-xl sm:text-2xl font-semibold text-gray-800 leading-tight">{solution.title}</h3>
                </div>
                <ul className="space-y-1.5 sm:space-y-2 flex-grow">
                  {solution.descriptions.map((desc, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <span className="text-[#0066ff] mt-1 flex-shrink-0">•</span>
                      <span className="text-xs sm:text-sm text-gray-600 leading-relaxed">{desc}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
